/**
 * Sysadmin Fine-Tuning → Adapters (Phase 5a infrastructure).
 *
 * Lists every tenant_lora_adapters row across all tenants. Sysadmin
 * can create a new adapter row (training_status='pending' until Phase
 * 5b's QLoRA worker picks it up) and manage the blue/green state
 * machine via Activate / Rollback.
 *
 * No "Train" button — that's Phase 5b.
 */

import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import {
  Sparkles,
  Plus,
  RefreshCw,
  CheckCircle2,
  Loader2,
  Power,
  Undo2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { StatTile } from '@/components/ui/stat-tile';
import { StatusPill } from '@/components/ui/status-pill';
import { PageError } from '@/components/ui/page-error';
import { EmptyState } from '@/components/ui/empty-state';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  useFtAdapters,
  useCreateFtAdapter,
  useActivateFtAdapter,
  useRollbackFtAdapter,
  type FtAdapterRow,
  type FtBlueGreenState,
} from '@/lib/use-sysadmin-fine-tuning';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/sysadmin/fine-tuning/adapters')({
  component: FtAdaptersPage,
});

function FtAdaptersPage() {
  const { data, isLoading, error, refetch, isFetching } = useFtAdapters();
  const [creating, setCreating] = useState(false);

  const items = data?.items ?? [];

  const counts = {
    total: items.length,
    active: items.filter((a) => a.blueGreenState === 'active').length,
    candidate: items.filter((a) => a.blueGreenState === 'candidate').length,
    training: items.filter((a) => a.trainingStatus === 'training').length,
    failed: items.filter((a) => a.trainingStatus === 'failed').length,
  };

  if (error) {
    return (
      <div className="p-6 max-w-6xl mx-auto">
        <PageError
          title="Failed to load adapters"
          message={error.message}
          onRetry={() => refetch()}
          retrying={isFetching}
        />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-5">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <Sparkles className="h-7 w-7 text-muted-foreground" />
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">
              LoRA Adapters
            </h1>
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              <Badge variant="outline" className="text-xs font-mono">
                Phase 5a
              </Badge>
              <span className="text-xs text-muted-foreground">
                Per-tenant adapter registry. Trainer + vLLM hot-swap land
                in Phase 5b.
              </span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => refetch()}
            disabled={isFetching}
          >
            <RefreshCw
              className={cn('h-3.5 w-3.5 mr-1.5', isFetching && 'animate-spin')}
            />
            Refresh
          </Button>
          <Button onClick={() => setCreating(true)}>
            <Plus className="h-3.5 w-3.5 mr-1.5" />
            New adapter
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <StatTile label="Total" value={String(counts.total)} icon={Sparkles} tone="default" />
        <StatTile label="Active" value={String(counts.active)} icon={CheckCircle2} tone={counts.active > 0 ? 'success' : 'muted'} />
        <StatTile label="Candidates" value={String(counts.candidate)} icon={Power} tone={counts.candidate > 0 ? 'default' : 'muted'} />
        <StatTile label="Training" value={String(counts.training)} icon={Loader2} tone={counts.training > 0 ? 'warning' : 'muted'} />
        <StatTile label="Failed" value={String(counts.failed)} icon={Undo2} tone={counts.failed > 0 ? 'destructive' : 'muted'} />
      </div>

      {isLoading && items.length === 0 ? (
        <div className="py-10 text-center text-muted-foreground text-sm animate-pulse">
          Loading adapters…
        </div>
      ) : items.length === 0 ? (
        <EmptyState
          icon={Sparkles}
          title="No adapters yet"
          body="Create an adapter row to register a planned QLoRA training job. The trainer arrives in Phase 5b; Phase 5a holds the registry, evaluation, and blue/green state machine."
        />
      ) : (
        <div className="rounded-md border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground">
                <th className="px-3 py-2 font-medium">Name</th>
                <th className="px-3 py-2 font-medium">Tenant</th>
                <th className="px-3 py-2 font-medium">Base Model</th>
                <th className="px-3 py-2 font-medium">Training</th>
                <th className="px-3 py-2 font-medium">Routing</th>
                <th className="px-3 py-2 font-medium text-right">Score</th>
                <th className="px-3 py-2 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((a) => (
                <AdapterRow key={a.id} adapter={a} />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {creating && (
        <CreateAdapterDialog
          open={creating}
          onOpenChange={setCreating}
          onSuccess={() => refetch()}
        />
      )}
    </div>
  );
}

function AdapterRow({ adapter }: { adapter: FtAdapterRow }) {
  const activate = useActivateFtAdapter();
  const rollback = useRollbackFtAdapter();

  return (
    <tr className="border-t hover:bg-muted/30 transition-colors">
      <td className="px-3 py-2.5">
        <div className="font-medium">{adapter.name}</div>
        {adapter.description && (
          <div className="text-xs text-muted-foreground truncate max-w-md" title={adapter.description}>
            {adapter.description}
          </div>
        )}
      </td>
      <td className="px-3 py-2.5 font-mono text-[11px] text-muted-foreground">
        {adapter.tenantId.slice(0, 8)}
      </td>
      <td className="px-3 py-2.5 text-xs">{adapter.baseModelId}</td>
      <td className="px-3 py-2.5">
        <TrainingStatusPill status={adapter.trainingStatus} />
      </td>
      <td className="px-3 py-2.5">
        <BlueGreenPill state={adapter.blueGreenState} />
      </td>
      <td className="px-3 py-2.5 text-right font-mono text-xs">
        {adapter.goldenDatasetScore != null
          ? adapter.goldenDatasetScore.toFixed(3)
          : '—'}
      </td>
      <td className="px-3 py-2.5 text-right">
        <div className="inline-flex items-center gap-1">
          <Button
            variant="outline"
            size="sm"
            onClick={() => activate.mutate({ id: adapter.id })}
            disabled={
              adapter.blueGreenState !== 'candidate' || activate.isPending
            }
            title={
              adapter.blueGreenState === 'candidate'
                ? 'Promote candidate → active'
                : `Cannot activate from state '${adapter.blueGreenState}'`
            }
          >
            <Power className="h-3.5 w-3.5" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() =>
              rollback.mutate({ id: adapter.id, reason: 'manual rollback' })
            }
            disabled={
              adapter.blueGreenState !== 'active' || rollback.isPending
            }
            title={
              adapter.blueGreenState === 'active'
                ? 'Rollback active → rolled_back'
                : `Cannot rollback from state '${adapter.blueGreenState}'`
            }
          >
            <Undo2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      </td>
    </tr>
  );
}

function TrainingStatusPill({
  status,
}: {
  status: FtAdapterRow['trainingStatus'];
}) {
  switch (status) {
    case 'pending':
      return (
        <StatusPill status="muted" compact>
          Pending
        </StatusPill>
      );
    case 'queued':
      return (
        <StatusPill status="muted" compact>
          Queued
        </StatusPill>
      );
    case 'training':
      return (
        <StatusPill status="info" compact icon={Loader2}>
          Training
        </StatusPill>
      );
    case 'complete':
      return (
        <StatusPill status="healthy" compact icon={CheckCircle2}>
          Complete
        </StatusPill>
      );
    case 'failed':
      return (
        <StatusPill status="destructive" compact>
          Failed
        </StatusPill>
      );
  }
}

function BlueGreenPill({ state }: { state: FtBlueGreenState }) {
  switch (state) {
    case 'inactive':
      return (
        <StatusPill status="muted" compact>
          Inactive
        </StatusPill>
      );
    case 'candidate':
      return (
        <StatusPill status="info" compact>
          Candidate
        </StatusPill>
      );
    case 'active':
      return (
        <StatusPill status="healthy" compact icon={Power}>
          Active
        </StatusPill>
      );
    case 'retired':
      return (
        <StatusPill status="muted" compact>
          Retired
        </StatusPill>
      );
    case 'rolled_back':
      return (
        <StatusPill status="destructive" compact icon={Undo2}>
          Rolled back
        </StatusPill>
      );
  }
}

// ─── Create dialog ──────────────────────────────────────────────────────

function CreateAdapterDialog({
  open,
  onOpenChange,
  onSuccess,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onSuccess: () => void;
}) {
  const [tenantId, setTenantId] = useState('');
  const [baseModelId, setBaseModelId] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [submitError, setSubmitError] = useState<string | null>(null);

  const create = useCreateFtAdapter();

  const tenantValid = /^[0-9a-f-]{36}$/.test(tenantId);
  const canSubmit =
    tenantValid &&
    baseModelId.trim().length > 0 &&
    name.trim().length > 0 &&
    !create.isPending;

  const handleSubmit = async () => {
    setSubmitError(null);
    try {
      await create.mutateAsync({
        tenantId,
        baseModelId: baseModelId.trim(),
        name: name.trim(),
        description: description.trim() || null,
      });
      onSuccess();
      onOpenChange(false);
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : String(err));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>New LoRA adapter</DialogTitle>
          <DialogDescription>
            Registers a planned QLoRA training job. The trainer worker
            (Phase 5b) reads training_status='pending' rows and runs
            QLoRA against approved training_data. Phase 5a holds the
            registry only.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold">
              Tenant ID <span className="text-red-500">*</span>
            </Label>
            <Input
              placeholder="00000000-0000-0000-0000-000000000000"
              value={tenantId}
              onChange={(e) => setTenantId(e.target.value)}
              className="font-mono text-xs"
            />
            {tenantId.length > 0 && !tenantValid && (
              <p className="text-[10px] text-destructive">Must be a valid UUID.</p>
            )}
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs font-semibold">
              Base Model ID <span className="text-red-500">*</span>
            </Label>
            <Input
              placeholder="argus-pro-72b"
              value={baseModelId}
              onChange={(e) => setBaseModelId(e.target.value)}
            />
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs font-semibold">
              Name <span className="text-red-500">*</span>
            </Label>
            <Input
              placeholder="Q1 maintenance fine-tune"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs font-semibold">Description</Label>
            <Textarea
              placeholder="Optional notes about the training data + intent"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
            />
          </div>

          {submitError && (
            <div className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive">
              {submitError}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!canSubmit}>
            {create.isPending && (
              <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
            )}
            Create
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
